import { BaseClient } from '../client/BaseClient';
import {
  Message,
  SendMessageRequest,
  ListMessagesParams,
  InteractiveMessage,
  Poll,
  PollVote,
} from '../types';

export class Messages {
  constructor(private client: BaseClient) {}

  /**
   * Send a text or media message
   * @param data - Message data
   */
  async send(data: SendMessageRequest): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/send', data);
    
    return response.message;
  }

  /**
   * Send a text message (convenience method)
   * @param tel - Sender phone number
   * @param to - Recipient ID
   * @param text - Message text
   * @param options - Additional options
   */
  async sendText(
    tel: string,
    to: string,
    text: string,
    options?: SendMessageRequest['options']
  ): Promise<Message> {
    return this.send({ tel, to, text, options });
  }

  /**
   * Send a media message (convenience method)
   * @param tel - Sender phone number
   * @param to - Recipient ID
   * @param media - Base64 encoded media
   * @param mimetype - Media MIME type
   * @param filename - Media filename
   * @param caption - Optional caption
   */
  async sendMedia(
    tel: string,
    to: string,
    media: string,
    mimetype: string,
    filename: string,
    caption?: string
  ): Promise<Message> {
    return this.send({ tel, to, media, mimetype, filename, caption });
  }

  /**
   * Send an interactive message (buttons or list)
   * @param tel - Sender phone number
   * @param to - Recipient ID
   * @param interactive - Interactive message data
   */
  async sendInteractive(
    tel: string,
    to: string,
    interactive: InteractiveMessage
  ): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/interactive', {
      tel,
      to,
      interactive,
    });
    
    return response.message;
  }

  /**
   * Send a poll
   * @param tel - Sender phone number
   * @param to - Recipient ID
   * @param poll - Poll data
   */
  async sendPoll(
    tel: string,
    to: string,
    poll: Poll
  ): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/poll', {
      tel,
      to,
      poll,
    });
    
    return response.message;
  }

  /**
   * Vote on a poll
   * @param tel - Voter phone number
   * @param vote - Vote data
   */
  async votePoll(tel: string, vote: PollVote): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/messages/poll/vote', {
      tel,
      ...vote,
    });
  }

  /**
   * List messages with filtering and pagination
   * @param params - Query parameters
   */
  async list(params?: ListMessagesParams): Promise<{
    messages: Message[];
    count: number;
    total: number;
  }> {
    const response = await this.client['get']<{
      success: boolean;
      messages: Message[];
      count: number;
      total: number;
    }>('/api/messages', { params });
    
    return {
      messages: response.messages,
      count: response.count,
      total: response.total,
    };
  }

  /**
   * Get a single message by ID
   * @param id - Message database ID
   */
  async get(id: string): Promise<Message> {
    const response = await this.client['get']<{
      success: boolean;
      message: Message;
    }>(`/api/messages/${id}`);
    
    return response.message;
  }

  /**
   * Download media from a message
   * @param id - Message database ID
   */
  async downloadMedia(id: string): Promise<{
    data: string;
    mimetype: string;
    filename: string;
  }> {
    const response = await this.client['get']<{
      success: boolean;
      data: string;
      mimetype: string;
      filename: string;
    }>(`/api/messages/${id}/media`);
    
    return {
      data: response.data,
      mimetype: response.mimetype,
      filename: response.filename,
    };
  }

  /**
   * Delete a message
   * @param tel - Phone number
   * @param messageId - WhatsApp message ID
   * @param forEveryone - Delete for everyone
   */
  async delete(
    tel: string,
    messageId: string,
    forEveryone: boolean = false
  ): Promise<{ success: boolean }> {
    return this.client['delete']<{ success: boolean }>('/api/messages', {
      data: { tel, messageId, forEveryone },
    });
  }

  /**
   * React to a message
   * @param tel - Phone number
   * @param messageId - Message to react to
   * @param emoji - Emoji reaction
   */
  async react(
    tel: string,
    messageId: string,
    emoji: string
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(`/api/messages/${messageId}/reactions`, {
      tel,
      emoji,
    });
  }

  /**
   * Remove a reaction from a message
   * @param tel - Phone number
   * @param messageId - Message to remove reaction from
   */
  async removeReaction(
    tel: string,
    messageId: string
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(`/api/messages/${messageId}/reactions/remove`, {
      tel,
    });
  }

  /**
   * Get reactions for a message
   * @param messageId - Message ID to get reactions for
   */
  async getReactions(messageId: string): Promise<{
    success: boolean;
    data: Array<{
      emoji: string;
      from: string;
      timestamp: number;
    }>;
  }> {
    return this.client['get']<{
      success: boolean;
      data: Array<{
        emoji: string;
        from: string;
        timestamp: number;
      }>;
    }>(`/api/messages/${messageId}/reactions`);
  }

  /**
   * Edit a message
   * @param tel - Phone number
   * @param messageId - Message to edit
   * @param newText - New message text
   */
  async edit(
    tel: string,
    messageId: string,
    newText: string
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/messages/edit', {
      tel,
      messageId,
      newText,
    });
  }

  /**
   * Pin a message
   * @param tel - Phone number
   * @param messageId - Message to pin
   * @param duration - Pin duration in seconds (0 = forever)
   */
  async pin(
    tel: string,
    messageId: string,
    duration: number = 0
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/messages/pin', {
      tel,
      messageId,
      duration,
    });
  }

  /**
   * Unpin a message
   * @param tel - Phone number
   * @param messageId - Message to unpin
   */
  async unpin(
    tel: string,
    messageId: string
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/messages/unpin', {
      tel,
      messageId,
    });
  }

  /**
   * Star a message
   * @param tel - Phone number
   * @param messageId - Message to star
   */
  async star(
    tel: string,
    messageId: string
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/messages/star', {
      tel,
      messageId,
      star: true,
    });
  }

  /**
   * Unstar a message
   * @param tel - Phone number
   * @param messageId - Message to unstar
   */
  async unstar(
    tel: string,
    messageId: string
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/messages/star', {
      tel,
      messageId,
      star: false,
    });
  }

  /**
   * Forward messages
   * @param tel - Phone number
   * @param to - Recipient IDs
   * @param messageIds - Messages to forward
   */
  async forward(
    tel: string,
    to: string[],
    messageIds: string[]
  ): Promise<{ success: boolean; count: number }> {
    const response = await this.client['post']<{
      success: boolean;
      count: number;
    }>('/api/messages/forward', {
      tel,
      to,
      messageIds,
    });
    
    return response;
  }

  /**
   * Send typing indicator
   * @param tel - Phone number
   * @param to - Chat ID
   * @param typing - true for typing, false for paused
   */
  async sendTyping(
    tel: string,
    to: string,
    typing: boolean = true
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/messages/typing', {
      tel,
      chatId: to,
      typing,
    });
  }

  /**
   * Mark messages as read
   * @param tel - Phone number
   * @param messageIds - Message IDs to mark as read
   */
  async markRead(
    tel: string,
    messageIds: string[]
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/messages/read', {
      tel,
      messageIds,
    });
  }

  /**
   * Send voice note
   * @param tel - Phone number
   * @param to - Recipient ID
   * @param audioData - Base64 encoded audio data
   * @param mimetype - Audio mimetype
   * @param duration - Duration in seconds
   */
  async sendVoiceNote(
    tel: string,
    to: string,
    audioData: string,
    mimetype: string = 'audio/ogg; codecs=opus',
    duration?: number
  ): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/voice', {
      tel,
      to,
      audioData,
      mimetype,
      duration,
    });
    
    return response.message;
  }

  /**
   * Send location message
   * @param tel - Phone number
   * @param to - Recipient ID
   * @param latitude - Latitude coordinate
   * @param longitude - Longitude coordinate
   * @param name - Location name
   * @param address - Location address
   */
  async sendLocation(
    tel: string,
    to: string,
    latitude: number,
    longitude: number,
    name?: string,
    address?: string
  ): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/location', {
      tel,
      to,
      latitude,
      longitude,
      name,
      address,
    });
    
    return response.message;
  }

  /**
   * Send live location message
   * @param tel - Phone number
   * @param to - Recipient ID
   * @param latitude - Latitude coordinate
   * @param longitude - Longitude coordinate
   * @param duration - Sharing duration in seconds
   */
  async sendLiveLocation(
    tel: string,
    to: string,
    latitude: number,
    longitude: number,
    duration: number = 3600
  ): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/live-location', {
      tel,
      to,
      latitude,
      longitude,
      duration,
    });
    
    return response.message;
  }

  /**
   * Send buttons message
   * @param tel - Phone number
   * @param to - Recipient ID
   * @param text - Message text
   * @param buttons - Array of buttons
   * @param headerText - Optional header text
   * @param footerText - Optional footer text
   */
  async sendButtons(
    tel: string,
    to: string,
    text: string,
    buttons: Array<{
      id: string;
      text: string;
      type?: 'reply' | 'call' | 'url';
      data?: string;
    }>,
    headerText?: string,
    footerText?: string
  ): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/buttons', {
      tel,
      to,
      text,
      buttons,
      headerText,
      footerText,
    });
    
    return response.message;
  }

  /**
   * Send list message
   * @param tel - Phone number
   * @param to - Recipient ID
   * @param title - List title
   * @param buttonText - Button text
   * @param sections - List sections
   */
  async sendList(
    tel: string,
    to: string,
    title: string,
    buttonText: string,
    sections: Array<{
      title: string;
      rows: Array<{
        id: string;
        title: string;
        description?: string;
      }>;
    }>
  ): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/list', {
      tel,
      to,
      title,
      buttonText,
      sections,
    });
    
    return response.message;
  }

  /**
   * Send sticker message
   * @param tel - Phone number
   * @param to - Recipient ID
   * @param stickerData - Base64 encoded sticker data (WebP format)
   */
  async sendSticker(
    tel: string,
    to: string,
    stickerData: string
  ): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/sticker', {
      tel,
      to,
      stickerData,
    });
    
    return response.message;
  }

  /**
   * Send album/multiple media
   * @param tel - Phone number
   * @param to - Recipient ID
   * @param media - Array of media items
   * @param caption - Shared caption
   */
  async sendAlbum(
    tel: string,
    to: string,
    media: Array<{
      data: string;
      mimetype: string;
      filename?: string;
      caption?: string;
    }>,
    caption?: string
  ): Promise<Message> {
    const response = await this.client['post']<{
      success: boolean;
      message: Message;
    }>('/api/messages/album', {
      tel,
      to,
      media,
      caption,
    });
    
    return response.message;
  }
}